import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Mail, Lock, AlertCircle, Loader2, ShieldCheck, ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_digital-seniors-3/artifacts/fqevhgzz_Elderra%20logo%20new%20original%20%281%29.png';

const AdminLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/login`,
        { email, password }
      );
      localStorage.setItem('admin_token', res.data.access_token);
      localStorage.setItem('admin_email', res.data.email);
      navigate('/admin');
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', width: '100%', display: 'flex', fontFamily: 'Manrope, system-ui, -apple-system, sans-serif', background: '#f7f5f2' }}>
      <SEO title="Admin Login - Elderra" description="Admin access to Elderra management dashboard" />

      {/* Left Brand Panel */}
      <div
        style={{
          flex: '1 1 50%',
          background: 'linear-gradient(160deg, #1a0b2e 0%, #2d1b4e 45%, #e5266a 120%)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          padding: '48px 56px',
          position: 'relative',
          overflow: 'hidden',
          color: '#fff',
        }}
        className="adm-login-brand"
      >
        {/* Subtle grain overlay */}
        <div style={{ position: 'absolute', inset: 0, opacity: 0.08, background: 'radial-gradient(circle at 20% 30%, #ffd600 0%, transparent 40%), radial-gradient(circle at 80% 70%, #e5266a 0%, transparent 45%)', pointerEvents: 'none' }} />

        <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ background: '#fff', padding: 8, borderRadius: 12, width: 56, height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src={LOGO_URL} alt="Elderra" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
          </div>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.02em' }}>elderra.io</div>
            <div style={{ fontSize: 11, opacity: 0.7, letterSpacing: '0.12em', textTransform: 'uppercase' }}>Admin Console</div>
          </div>
        </div>

        <div style={{ position: 'relative', zIndex: 1, maxWidth: 480 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)', padding: '6px 14px', borderRadius: 999, fontSize: 12, fontWeight: 600, letterSpacing: '0.04em', marginBottom: 22 }}>
            <ShieldCheck size={14} /> SECURE ACCESS
          </div>
          <h1 style={{ fontSize: 44, fontWeight: 800, lineHeight: 1.08, letterSpacing: '-0.03em', margin: 0 }}>
            Run Elderra's<br />
            <span style={{ background: 'linear-gradient(90deg, #ffd600, #e5266a)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>operations hub</span>
          </h1>
          <p style={{ marginTop: 18, fontSize: 15, lineHeight: 1.6, opacity: 0.8 }}>
            Manage bookings, appointment slots, YouTuber signups, fundraising campaigns, WhatsApp groups, and site content — all from one place.
          </p>
        </div>

        <div style={{ position: 'relative', zIndex: 1, display: 'flex', gap: 24, fontSize: 12, opacity: 0.6 }}>
          <span>© 2026 Giant-Tree Foundation</span>
          <span>·</span>
          <span>80G URN: AAMCG7420MF20261</span>
        </div>
      </div>

      {/* Right Form Panel */}
      <div style={{ flex: '1 1 50%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 32px', background: '#fff' }}>
        <div style={{ width: '100%', maxWidth: 420 }}>
          <div style={{ marginBottom: 32 }}>
            <h2 style={{ fontSize: 28, fontWeight: 800, color: '#111827', letterSpacing: '-0.02em', margin: 0 }}>Welcome back</h2>
            <p style={{ color: '#6b7280', fontSize: 15, marginTop: 6 }}>Sign in to your admin dashboard</p>
          </div>

          {error && (
            <div data-testid="admin-login-error" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, color: '#991b1b', fontSize: 13, marginBottom: 20 }}>
              <AlertCircle size={18} style={{ flexShrink: 0 }} /><span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 7 }}>
                <Mail size={14} /> Email Address
              </label>
              <input
                data-testid="admin-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                placeholder="admin@elderra.io"
                autoComplete="email"
                style={{
                  width: '100%', padding: '12px 14px', border: '1.5px solid #e5e7eb',
                  borderRadius: 10, fontSize: 15, background: loading ? '#f9fafb' : '#fff',
                  outline: 'none', transition: 'border-color 0.15s, box-shadow 0.15s',
                  boxSizing: 'border-box', fontFamily: 'inherit',
                }}
                onFocus={(e) => { e.target.style.borderColor = '#e5266a'; e.target.style.boxShadow = '0 0 0 3px rgba(229,38,106,0.12)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; }}
              />
            </div>

            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 7 }}>
                <Lock size={14} /> Password
              </label>
              <input
                data-testid="admin-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                placeholder="Enter your password"
                autoComplete="current-password"
                style={{
                  width: '100%', padding: '12px 14px', border: '1.5px solid #e5e7eb',
                  borderRadius: 10, fontSize: 15, background: loading ? '#f9fafb' : '#fff',
                  outline: 'none', transition: 'border-color 0.15s, box-shadow 0.15s',
                  boxSizing: 'border-box', fontFamily: 'inherit',
                }}
                onFocus={(e) => { e.target.style.borderColor = '#e5266a'; e.target.style.boxShadow = '0 0 0 3px rgba(229,38,106,0.12)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; }}
              />
            </div>

            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={loading}
              style={{
                marginTop: 6, padding: '14px 20px', width: '100%',
                background: loading ? '#9ca3af' : '#111827',
                color: '#fff', border: 'none', borderRadius: 10,
                fontSize: 15, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                transition: 'background 0.15s, transform 0.15s', fontFamily: 'inherit',
              }}
              onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = '#000'; }}
              onMouseLeave={(e) => { if (!loading) e.currentTarget.style.background = '#111827'; }}
            >
              {loading ? <><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Signing in...</> : <>Sign In <ArrowRight size={16} /></>}
            </button>
          </form>

          <div style={{ marginTop: 28, paddingTop: 20, borderTop: '1px solid #f3f4f6', fontSize: 12, color: '#9ca3af', textAlign: 'center' }}>
            Authorized personnel only · All login attempts are logged
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (max-width: 900px) {
          .adm-login-brand { display: none !important; }
        }
      `}</style>
    </div>
  );
};

export default AdminLogin;
